import React, { useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Receipt as ReceiptIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import getRefundService from "../../../services/getRefundService";
import { getCookie } from "../../../function"; // Ensure getCookie is imported
import { jwtDecode } from "jwt-decode"; // Ensure jwtDecode is imported
import axios from "axios";
import getArticleService from "../../../services/getArticleService";
import { API_URL } from "../../../config";

// Helper function to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
  }).format(amount);
};

// Helper function to format date
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

// Helper function to get status color
const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "pending":
      return "warning";
    case "approved":
      return "info";

    case "rejected":
      return "error";
    case "completed":
      return "success";
    default:
      return "default";
  }
};

// Custom hook for fetching refunds
function useRefundRequests(status) {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(getCookie("token")); // Get token from cookie
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [perPage, setPerPage] = useState(10); // State to store items per page, defaults to 10
  const decodedToken = token ? jwtDecode(token) : null;
  const journalIds = decodedToken?.user?.journal || "";

  const userRole = decodedToken?.user?.userType || ""; // Use a more generic name for role for clarity

  const fetchRefunds = useCallback(
    async (pageNum = 1) => {
      if (loading) return;
      setLoading(true);

      try {
        const result = await getRefundService.fetchRefundRequests({
          status,
          page: pageNum,
          limit: perPage,
          journalIds,
        });

        console.log("Full API Response:", result);
        console.log("Refund Data:", result.data);

        if (result) {
          // Log the first refund item to see its structure
          if (result.data && result.data.length > 0) {
            console.log("First Refund Item:", result.data[0]);
            console.log("Article Data:", result.data[0].article);
          }

          setRefunds(result.data);
          setTotalItems(result.total);
          setTotalPages(result.totalPages);
          setPerPage(result.perPage || 10);
        }
      } catch (error) {
        console.error("Error loading refunds:", error);
        NotificationManager.error("Failed to load refund requests");
      } finally {
        setLoading(false);
      }
    },
    [status, perPage]
  );

  const refreshRefunds = useCallback(() => {
    setRefunds([]);
    fetchRefunds(page);
  }, [fetchRefunds, page]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchRefunds(newPage);
  };

  // Reset and fetch when status changes
  React.useEffect(() => {
    setRefunds([]);
    setPage(1);
    fetchRefunds(1);
  }, [status, fetchRefunds]);

  return {
    refunds,
    loading,
    refreshRefunds,
    page,
    totalPages,
    totalItems,
    handlePageChange,
    perPage,
    userRole, // Export userRole from the hook
  };
}

const RefundRequestAccordion = ({
  refund,
  userRole,
  onUpdateStatus,
  onEditorUpdate,
  onViewProof,
  onEditBank,
  onEditCompletion,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (event, isExpanded) => {
    setExpanded(isExpanded);
  };

  const getTotalPayments = (payments) => {
    return (
      payments?.reduce((sum, payment) => {
        const amount = parseFloat(payment.M?.amount?.N || 0);
        return sum + amount;
      }, 0) || 0
    );
  };

  return (
    <Accordion expanded={expanded} onChange={handleChange}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Article ID
            </Typography>
            <Typography variant="body1" noWrap>
              {refund.articleId || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Author
            </Typography>
            <Typography variant="body1" noWrap>
              {refund.article?.authorName || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Client Name
            </Typography>
            <Typography variant="body1" noWrap>
              {refund.clientDetails?.first_name &&
              refund.clientDetails?.last_name
                ? `${refund.clientDetails.first_name} ${refund.clientDetails.last_name}`
                : "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Publication
            </Typography>
            <Typography variant="body1" noWrap>
              {refund.publicationDetails?.name || "N/A"}
            </Typography>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Status
              </Typography>
              <Chip
                label={refund.status}
                color={getStatusColor(refund.status)}
                size="small"
                sx={{
                  minWidth: "80px",
                  "& .MuiChip-label": {
                    px: 1,
                    fontSize: "0.75rem",
                  },
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={2}>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
                mt: 1,
              }}
            >
              {userRole === "admin" &&
                refund.status.toLowerCase() === "pending" && (
                  <>
                    <IconButton
                      color="success"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateStatus(refund.id, "accept");
                      }}
                      size="small"
                    >
                      <CheckCircleIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateStatus(refund.id, "reject");
                      }}
                      size="small"
                    >
                      <CancelIcon />
                    </IconButton>
                  </>
                )}
              {userRole === "user" &&
                refund.status.toLowerCase() === "approved" && (
                  <IconButton
                    color="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditorUpdate(refund.id);
                    }}
                    size="small"
                    title="Update Refund Details"
                  >
                    <ReceiptIcon />
                  </IconButton>
                )}
            </Box>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails>
        <Grid container spacing={3}>
          {/* Article Details Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Article Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Journal Name
                </Typography>
                <Typography variant="body1">
                  {refund.article?.journalName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Service Name
                </Typography>
                <Typography variant="body1">
                  {refund.article?.serviceName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Publication Type
                </Typography>
                <Typography variant="body1">
                  {refund.article?.publicationType || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Payment Details Section */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Fees
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(refund.article?.totalfees || 0)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Payments Received
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(getTotalPayments(refund.article?.payments))}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="text.secondary">
                  Refund Amount Requested
                </Typography>
                <Typography variant="body1">
                  {formatCurrency(refund.refundAmount || 0)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Refund Request Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Refund Request Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Refund Reason
                </Typography>
                <Typography variant="body1">
                  {refund.refundReason || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">
                  Request Date
                </Typography>
                <Typography variant="body1">
                  {formatDate(refund.createdAt)}
                </Typography>
              </Grid>
            </Grid>
          </Grid>

          {/* Bank Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Bank Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Beneficiary Name
                </Typography>
                <Typography variant="body1">
                  {refund.beneficiaryName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Bank Name
                </Typography>
                <Typography variant="body1">
                  {refund.bankName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  Account Number
                </Typography>
                <Typography variant="body1">
                  {refund.accountNumber || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="subtitle2" color="text.secondary">
                  IFSC Code
                </Typography>
                
                <Grid container alignItems="center">
                  <Grid item xs>
                    <Typography variant="body1">
                      {refund.ifscCode || "N/A"}
                    </Typography>
                    
                  </Grid>
                  {userRole === "user" && refund.status.toLowerCase() === "approved" && (
                    <Grid item>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditBank(refund);
                        }}
                        color="primary"
                        variant="outlined"
                        sx={{
                          ml: 2,
                          mt: 1,
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.dark !important',
                            color: 'primary.dark !important',
                            backgroundColor: 'transparent',
                          },
                        }}
                      >
                        Edit
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Completed Refund Details - Only show if status is completed */}
          {refund.status.toLowerCase() === "completed" && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                Completion Details
                {userRole === "user" && refund.status.toLowerCase() === "completed" && (
                 <Button
                 size="small"
                 onClick={(e) => {
                   e.stopPropagation();
                   onEditCompletion(refund);
                 }}
                 color="primary"
                 variant="outlined"
                 sx={{
                   ml: 2,
                   borderColor: 'primary.main',
                   color: 'primary.main',
                   '&:hover': {
                     borderColor: 'primary.dark !important',
                     color: 'primary.dark !important',
                     backgroundColor: 'transparent',
                   },
                 }}
               >
                 Edit
               </Button>
                )}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Editor Remark
                  </Typography>
                  <Typography variant="body1">
                    {refund.editorRemark || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Refund Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(refund.dateOfRefund)}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment Proof
                  </Typography>
                  {refund.RefundProofKey && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onViewProof(refund.RefundProofKey)}
                    >
                      View Proof
                    </Button>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

// New component for displaying refund details in a modal
const RefundDetailsModal = ({ open, onClose, refund }) => {
  if (!refund) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Refund Details</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Article ID
              </Typography>
              <Typography variant="body1">
                {refund.articleId || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Article Author
              </Typography>
              <Typography variant="body1">
                {refund.article?.authorName || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Journal Name
              </Typography>
              <Typography variant="body1">
                {refund.article?.journalName || "N/A"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Fees Received
              </Typography>
              <Typography variant="body1">
                ₹{refund.article?.feesRecived || "0.00"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Total Fees
              </Typography>
              <Typography variant="body1">
                ₹{refund.article?.totalfees || "0.00"}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Refund Amount
              </Typography>
              <Typography variant="body1">
                ₹{refund.refundAmount || "0.00"}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const updateRefundDetails = async ({ id, ...fields }) => {
  const response = await axios.post(
    `${API_URL}/updaterefund?id=${id}`,
    fields,
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: getCookie("token"),
        "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
      },
    }
  );
  return response.data;
};

const RefundList = () => {
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [adminRemark, setAdminRemark] = useState("");
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [currentRefundDetails, setCurrentRefundDetails] = useState(null);
  const [openEditorDialog, setOpenEditorDialog] = useState(false);
  const [paymentProof, setPaymentProof] = useState(null);
  const [editorRemark, setEditorRemark] = useState("");
  const [refundDate, setRefundDate] = useState("");
  const [paymentProofPreview, setPaymentProofPreview] = useState(null);
  const [openProofDialog, setOpenProofDialog] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editDialogFields, setEditDialogFields] = useState({});
  const [editDialogStage, setEditDialogStage] = useState(""); // 'bank' or 'completion'
  const [editDialogRefundId, setEditDialogRefundId] = useState(null);
  const [editDialogLoading, setEditDialogLoading] = useState(false);

  const {
    refunds,
    loading,
    refreshRefunds,
    page,
    totalPages,
    totalItems,
    handlePageChange,
    perPage,
    userRole,
  } = useRefundRequests(selectedStatus === "all" ? "" : selectedStatus);

  const handleStatusChange = (event, newValue) => {
    setSelectedStatus(newValue);
  };

  const handleUpdateStatus = async (refundId, action) => {
    setSelectedRefund({ id: refundId, action });
    setOpenDialog(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!selectedRefund) return;

    try {
      const result = await getRefundService.updateRefundStatus({
        id: selectedRefund.id,
        action: selectedRefund.action,
        adminRemark: adminRemark,
      });

      console.log(result);

      if (result?.success) {
        refreshRefunds();
        setOpenDialog(false);
        setAdminRemark("");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      NotificationManager.error("Failed to update refund status");
    }
  };

  const handleOpenDetails = (refund) => {
    setCurrentRefundDetails(refund);
    setOpenDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsModal(false);
    setCurrentRefundDetails(null);
  };

  const handleEditorUpdate = (refundId) => {
    setSelectedRefund({ id: refundId });
    setOpenEditorDialog(true);
  };

  const handlePaymentProofChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPaymentProof(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPaymentProofPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditorSubmit = async () => {
    if (!selectedRefund) return;

    try {
      // First upload the file to S3
      // 1) UPLOAD ARTICLE FILE TO S3 VIA PRESIGNED URL
      let paymentProofUrl = "";
      if (paymentProof) {
        console.log("Requesting presigned URL for article file...");
        const presignResponse = await axios.post(
          "https://hquswoext6.execute-api.ap-south-1.amazonaws.com/prod/articles/presignedurl",
          {
            fileName: paymentProof.name,
            fileType: paymentProof.type,
            folderName: "refund",
          },
          {
            headers: {
              Authorization: getCookie("token"),
              "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
            },
          }
        );

        console.log("Presigned URL response (article):", presignResponse.data);
        const presignData =
          typeof presignResponse.data === "string"
            ? JSON.parse(presignResponse.data)
            : presignResponse.data;

        const { presignedUrl, fileKey } = presignData;

        if (!presignedUrl) {
          throw new Error("Presigned URL for article file is undefined");
        }

        await axios.put(presignedUrl, paymentProof, {
          headers: {
            "Content-Type": paymentProof.type,
          },
        });
        console.log("Refund file successfully uploaded with key:", fileKey);

        paymentProofUrl = fileKey;
      }

      // Then update the refund process with the S3 key and other details
      const result = await getRefundService.updateRefundProcess({
        id: selectedRefund.id,
        body: {
          RefundProofKey: paymentProofUrl,
          remark: editorRemark,
          dateOfRefund: refundDate,
        },
      });

      console.log(result);

      if (result?.success) {
        refreshRefunds();
        setOpenEditorDialog(false);
        setPaymentProof(null);
        setEditorRemark("");
        setRefundDate("");
        setPaymentProofPreview(null);
        NotificationManager.success(
          "Refund payment details updated successfully"
        );
      } else {
        NotificationManager.error(result.message || "Something went wrong!!!");
      }
    } catch (error) {
      console.error("Error updating payment details:", error);
      NotificationManager.error("Failed to update refund payment details");
    }
  };

  const handleViewProof = async (proofKey) => {
    try {
      const data = await getArticleService.getFilePreSIgnedUrl({
        key: proofKey,
      });
      setProofUrl(data);
      setSelectedProof(proofKey);
      setOpenProofDialog(true);
    } catch (error) {
      console.error("Error fetching proof URL:", error);
      NotificationManager.error("Failed to load payment proof");
    }
  };

  const handleCloseProofDialog = () => {
    setOpenProofDialog(false);
    setSelectedProof(null);
    setProofUrl(null);
  };

  // Open for bank details
  const handleOpenEditBank = (refund) => {
    setEditDialogFields({
      accountNumber: refund.accountNumber || "",
      ifscCode: refund.ifscCode || "",
      beneficiaryName: refund.beneficiaryName || "",
      bankName: refund.bankName || "",
    });
    setEditDialogStage("bank");
    setEditDialogRefundId(refund.id);
    setOpenEditDialog(true);
  };

  // Open for completion details
  const handleOpenEditCompletion = (refund) => {
    setEditDialogFields({
      editorRemark: refund.editorRemark || "",
      dateOfRefund: refund.dateOfRefund || "",
      RefundProofKey: refund.RefundProofKey || "",
    });
    setEditDialogStage("completion");
    setEditDialogRefundId(refund.id);
    setOpenEditDialog(true);
  };

  const handleEditDialogFieldChange = (e) => {
    const { name, value } = e.target;
    setEditDialogFields((prev) => ({ ...prev, [name]: value }));
  };

  // For file upload (RefundProofKey)
  const handleEditDialogFileChange = (e) => {
    const file = e.target.files[0];
    setEditDialogFields((prev) => ({ ...prev, RefundProofKey: file }));
  };

  const handleEditDialogSubmit = async () => {
    setEditDialogLoading(true);
    try {
      let RefundProofKey = editDialogFields.RefundProofKey;
      // If editing completion details and a new file is selected, upload to S3
      if (editDialogStage === "completion" && RefundProofKey instanceof File) {
        // Upload to S3 via presigned URL
        const presignResponse = await axios.post(
          "https://hquswoext6.execute-api.ap-south-1.amazonaws.com/prod/articles/presignedurl",
          {
            fileName: RefundProofKey.name,
            fileType: RefundProofKey.type,
            folderName: "refund",
          },
          {
            headers: {
              Authorization: getCookie("token"),
              "x-api-key": "JjgeTensKk20J7GIxBfMSaA25Aricvmr1HYmjxpS",
            },
          }
        );
        const presignData =
          typeof presignResponse.data === "string"
            ? JSON.parse(presignResponse.data)
            : presignResponse.data;
        const { presignedUrl, fileKey } = presignData;
        if (!presignedUrl) {
          throw new Error("Presigned URL for refund file is undefined");
        }
        await axios.put(presignedUrl, RefundProofKey, {
          headers: {
            "Content-Type": RefundProofKey.type,
          },
        });
        RefundProofKey = fileKey;
      }
      let { accountNumber, ifscCode, editorRemark, dateOfRefund, beneficiaryName, bankName } = editDialogFields;
      // Convert dateOfRefund from DD-MM-YYYY to YYYY-MM-DD if needed
      let formattedDateOfRefund = dateOfRefund;
      if (dateOfRefund && dateOfRefund.includes('-')) {
        const parts = dateOfRefund.split('-');
        if (parts.length === 3 && parts[2].length === 4) {
          // If format is DD-MM-YYYY, convert to YYYY-MM-DD
          formattedDateOfRefund = `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
        }
      }
      const payload = {
        id: editDialogRefundId,
        ...(accountNumber !== undefined ? { accountNumber } : {}),
        ...(ifscCode !== undefined ? { ifscCode } : {}),
        ...(editorRemark !== undefined ? { editorRemark } : {}),
        ...(formattedDateOfRefund ? { dateOfRefund: formattedDateOfRefund } : {}),
        ...(RefundProofKey ? { RefundProofKey } : {}),
        ...(beneficiaryName !== undefined ? { beneficiaryName } : {}),
        ...(bankName !== undefined ? { bankName } : {}),
      };
      const { id, ...fields } = payload;
      const response = await updateRefundDetails({ id, ...fields });
   
      const res = JSON.parse(response)
      const result  = JSON.parse(res.body)
      if (result.success) {
        NotificationManager.success("Details updated successfully");
        setOpenEditDialog(false);
        setEditDialogFields({});
        setEditDialogRefundId(null);
        setEditDialogStage("");
        await refreshRefunds();
      } else {
        NotificationManager.error("Failed to update details");
      }
    } catch (error) {
      NotificationManager.error("Error updating details");
    } finally {
      setEditDialogLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        sx={{
          p: 3,
          mb: 3,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5" component="h1">
            Refund Requests
          </Typography>
          <Button startIcon={<RefreshIcon />} onClick={refreshRefunds}>
            Refresh
          </Button>
        </Box>

        <Tabs
          value={selectedStatus}
          onChange={handleStatusChange}
          sx={{ mb: 3 }}
        >
          <Tab value="all" label="All" />
          <Tab value="pending" label="Pending" />
          <Tab value="approved" label="Approved" />
          <Tab value="rejected" label="Rejected" />
          <Tab value="completed" label="Completed" />
        </Tabs>

        <Box sx={{ flex: 1, overflowY: "auto", mb: 2 }}>
          {refunds.length === 0 && !loading ? (
            <Typography variant="body1" align="center" sx={{ py: 3 }}>
              No refund requests found
            </Typography>
          ) : (
            refunds.map((refund, index) => (
              <RefundRequestAccordion
                key={refund.id || index}
                refund={refund}
                userRole={userRole}
                onUpdateStatus={handleUpdateStatus}
                onEditorUpdate={handleEditorUpdate}
                onViewProof={handleViewProof}
                onEditBank={handleOpenEditBank}
                onEditCompletion={handleOpenEditCompletion}
              />
            ))
          )}

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>

        {/* Pagination Controls - Fixed at bottom */}
        {totalPages > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pt: 2,
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              backgroundColor: "background.paper",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {totalItems === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, totalItems)} of {totalItems} items
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      {/* Confirmation Dialog for Accept/Reject */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>
          Confirm {selectedRefund?.action === "accept" ? "Accept" : "Reject"}{" "}
          Refund
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to{" "}
            {selectedRefund?.action === "accept" ? "accept" : "reject"} this
            refund request?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Admin Remark (Optional)"
            value={adminRemark}
            onChange={(e) => setAdminRemark(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmStatusUpdate}
            variant="contained"
            color={selectedRefund?.action === "accept" ? "success" : "error"}
          >
            Confirm {selectedRefund?.action === "accept" ? "Accept" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refund Details Modal */}
      <RefundDetailsModal
        open={openDetailsModal}
        onClose={handleCloseDetails}
        refund={currentRefundDetails}
      />

      {/* New Editor Update Dialog */}
      <Dialog
        open={openEditorDialog}
        onClose={() => setOpenEditorDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Update Refund Payment Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Payment Proof
                </Typography>
                <Box
                  sx={{
                    border: "2px dashed #ccc",
                    borderRadius: 2,
                    p: 2,
                    textAlign: "center",
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                  onClick={() =>
                    document.getElementById("payment-proof-input").click()
                  }
                >
                  {paymentProofPreview ? (
                    <Box sx={{ position: "relative" }}>
                      <img
                        src={paymentProofPreview}
                        alt="Payment Proof Preview"
                        style={{ maxWidth: "100%", maxHeight: "200px" }}
                      />
                      <Button
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPaymentProof(null);
                          setPaymentProofPreview(null);
                        }}
                        sx={{ position: "absolute", top: 8, right: 8 }}
                      >
                        Remove
                      </Button>
                    </Box>
                  ) : (
                    <Typography>Click to upload payment proof</Typography>
                  )}
                  <input
                    id="payment-proof-input"
                    type="file"
                    accept="image/*"
                    onChange={handlePaymentProofChange}
                    style={{ display: "none" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Remark"
                  value={editorRemark}
                  onChange={(e) => setEditorRemark(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Refund Date"
                  value={refundDate}
                  onChange={(e) => setRefundDate(e.target.value)}
                  inputProps={{
                    max: new Date().toISOString().split("T")[0],
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditorDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEditorSubmit}
            variant="contained"
            color="primary"
            disabled={!paymentProof || !refundDate}
          >
            Update Payment Details
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update the Proof View Dialog */}
      <Dialog
        open={openProofDialog}
        onClose={handleCloseProofDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            p: 2,
          }}
        >
          Refund Payment Proof
          <IconButton
            aria-label="close"
            onClick={handleCloseProofDialog}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, height: "80vh" }}>
          {selectedProof && proofUrl && (
            <iframe
              src={proofUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: 4,
              }}
              title="Refund Proof"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {editDialogStage === "bank" ? "Bank Details" : "Completion Details"}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {editDialogStage === "bank" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Account Number"
                      name="accountNumber"
                      value={editDialogFields.accountNumber || ""}
                      onChange={handleEditDialogFieldChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="IFSC Code"
                      name="ifscCode"
                      value={editDialogFields.ifscCode || ""}
                      onChange={handleEditDialogFieldChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Beneficiary Name"
                      name="beneficiaryName"
                      value={editDialogFields.beneficiaryName || ""}
                      onChange={handleEditDialogFieldChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Bank Name"
                      name="bankName"
                      value={editDialogFields.bankName || ""}
                      onChange={handleEditDialogFieldChange}
                    />
                  </Grid>
                </>
              )}
              {editDialogStage === "completion" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Editor Remark"
                      name="editorRemark"
                      value={editDialogFields.editorRemark || ""}
                      onChange={handleEditDialogFieldChange}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Refund Date"
                      name="dateOfRefund"
                      value={editDialogFields.dateOfRefund || ""}
                      onChange={handleEditDialogFieldChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Payment Proof
                    </Typography>
                    <Button
                      variant="outlined"
                      component="label"
                      fullWidth
                    >
                      {editDialogFields.RefundProofKey instanceof File
                        ? editDialogFields.RefundProofKey.name
                        : editDialogFields.RefundProofKey
                        ? "Change File"
                        : "Upload File"}
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        hidden
                        onChange={handleEditDialogFileChange}
                      />
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            onClick={handleEditDialogSubmit}
            variant="contained"
            color="primary"
            disabled={editDialogLoading}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RefundList;
